import * as React from 'react';

import { HiveDataSyncState } from '@gitbook/hive-client';
import { useHiveClient } from '@gitbook/hive-react';
import { AlertBanner } from '@gitbook/spine-react';

import { setRealtimeSyncDisconnected, useIsRealtimeSyncDisconnected } from '../state';

/**
 * How long realtime sync must stay down before it is worth telling the user about.
 *
 * Longer than a single connect attempt (`CONNECT_TIMEOUT_MS` in `HiveDataSyncV2Manager`) plus the
 * first backoff delay, so a reconnection that recovers on its own never flashes a warning.
 */
const DISCONNECTED_GRACE_MS = 15_000;

/**
 * How often the set of open data syncs is re-scanned. State changes on the syncs already being
 * watched are observed directly, so this only exists to notice content opened since the last
 * scan — it delays detection by at most one interval.
 */
const SCAN_INTERVAL_MS = 5000;

/**
 * Warn that edits are no longer reaching GitBook.
 *
 * Local editing keeps working while realtime sync is down (changes accumulate in the document
 * and replay on reconnection), so this deliberately does not block the UI — but a connection
 * that never comes back loses everything written since it dropped, which is worth surfacing.
 */
export function RealtimeSyncBanner(): React.ReactElement | null {
    const isDisconnected = useIsRealtimeSyncDisconnected();

    if (!isDisconnected) {
        return null;
    }

    // Deliberately no action: reconnection is automatic, and the one thing a user would try
    // on their own — reloading — is what discards the changes that have not been sent yet.
    return (
        <AlertBanner
            kind="warning"
            title="Not syncing"
            details="Your changes are saved locally but aren't reaching GitBook yet. Trying to reconnect…"
        />
    );
}

/**
 * Watch every open data sync and report to {@link RealtimeSyncBanner} when any of them has been
 * down long enough to be worth a warning. Renders nothing.
 *
 * Watching the whole pool rather than the sync of whichever content is on screen means pages and
 * reusable content are covered as well as branches, from a single mount point.
 */
export function RealtimeSyncReporter(): null {
    const client = useHiveClient();

    React.useEffect(() => {
        const token = {};
        let downSince: number | null = null;
        let graceTimer: NodeJS.Timeout | undefined;
        let unlisten: Array<() => void> = [];

        const evaluate = () => {
            const isDown = client.sync.getAll().some((sync) => isDataSyncDown(sync.dataSync.state));

            if (!isDown) {
                downSince = null;
                clearTimeout(graceTimer);
                graceTimer = undefined;
                setRealtimeSyncDisconnected(token, false);
                return;
            }

            downSince ??= Date.now();
            const elapsed = Date.now() - downSince;

            if (elapsed >= DISCONNECTED_GRACE_MS) {
                setRealtimeSyncDisconnected(token, true);
                return;
            }

            // Come back exactly when the grace expires: a sync that stays down emits no further
            // state change, so nothing else would re-run this.
            graceTimer ??= setTimeout(() => {
                graceTimer = undefined;
                evaluate();
            }, DISCONNECTED_GRACE_MS - elapsed);
        };

        // Re-subscribing wholesale keeps this simple: the pool holds a handful of syncs, and
        // reading it is the only way to notice ones opened since the last scan.
        const scan = () => {
            unlisten.forEach((stop) => stop());
            // `listen` throws on a destroyed sync, so subscribe without awaiting anything in
            // between: the pool cannot evict a sync while this runs.
            unlisten = client.sync.getAll().map((sync) => sync.dataSync.listen('state', evaluate));
            evaluate();
        };

        scan();
        const interval = setInterval(scan, SCAN_INTERVAL_MS);

        return () => {
            clearInterval(interval);
            clearTimeout(graceTimer);
            unlisten.forEach((stop) => stop());
            setRealtimeSyncDisconnected(token, false);
        };
    }, [client]);

    return null;
}

/**
 * INACTIVE is the intentional close after five minutes idle, not a failure. Every other
 * non-OPEN state means changes are not reaching the server.
 */
function isDataSyncDown(state: HiveDataSyncState): boolean {
    return state !== HiveDataSyncState.OPEN && state !== HiveDataSyncState.INACTIVE;
}
